import {
  type Appointment,
  type Task,
  createAndUpdateTask,
  isTask,
} from "@repo/api";
import { t } from "@repo/translation";
import { type IIToastContext, type Toast, getErrorMessage } from "@repo/utils";
import { isBefore } from "date-fns";

import { isUnassignedLane } from "./constants";
import { type OutlookEvent } from "./types";

const getErrorObject = (err?: Error) =>
  ({
    title: t("prompt.task_saved_failed"),
    type: "error",
    message: err ? getErrorMessage(err) : undefined,
  }) as Toast;

function validateNewTask(newTask: Partial<Task>) {
  try {
    const startTime =
      newTask.taskStartDateTime && new Date(newTask.taskStartDateTime);
    const endTime =
      newTask.taskEndDateTime && new Date(newTask.taskEndDateTime);

    if (startTime && endTime && isBefore(endTime, startTime)) {
      throw new Error("End time is before start time");
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);

    return false;
  }
  return true;
}

export interface UpdateTaskData {
  id: string;
  emailId?: string;
  assignedTeam?: number;
  startDate: string;
  endDate: string;

  makeCopy?: boolean;
}
export const updateTask = async (
  task: Task | OutlookEvent | Appointment | undefined,
  taskData: UpdateTaskData,
  updateToast: IIToastContext["updateToast"]
) => {
  if (!task || !isTask(task)) {
    updateToast(getErrorObject());
    return;
  }

  const emailId = taskData.emailId ?? task.emailId;
  const assignedTeam = taskData.assignedTeam ?? task.assignedTeam;
  const isUnassigned = emailId ? isUnassignedLane(emailId) : false;

  const newTask = {
    ...task,
    id: taskData.makeCopy ? undefined : task.id,
    emailId: isUnassigned ? undefined : emailId,
    assignedTeam: isUnassigned ? assignedTeam : undefined,
    taskStartDateTime: task.isTaskDueBy ? undefined : taskData.startDate,
    taskEndDateTime: task.isTaskDueBy ? undefined : taskData.endDate,
    taskDate: task.isTaskDueBy ? taskData.startDate : undefined,
  };

  if (!validateNewTask(newTask)) {
    updateToast({
      title: t("prompt.task_saved_failed"),
      type: "error",
    });
  }

  await createAndUpdateTask(newTask)
    .then(() => {
      updateToast({
        id: task.id,
        title: t("prompt.task_saved"),
        button: {
          title: t("action.undo"),
          action: () => {
            createAndUpdateTask({
              ...task,
            })
              .then((res) => {
                updateToast({
                  id: res.id,
                  title: t("prompt.task_saved"),
                });
              })
              .catch((err) => {
                updateToast(getErrorObject(err));
              });
          },
        },
      });
    })
    .catch((err) => {
      updateToast(getErrorObject(err));
    });
};
